import * as XLSX from 'xlsx';
import { formatDate } from '../utils/constants';

const hasSection = (fieldsConfig, section) => !fieldsConfig || section in fieldsConfig;

const VCG_HEADERS = [
  'Filename', 'Order Number', 'Delivery Date', 'Customer Name', 'Customer Address',
  'Part Code', 'Quantity', 'Price', 'Subtotal', 'Total', 'Observaciones', 'Drawing', 'Currency'
];

const EXPORT_TEMPLATES = {
  invoice: exportInvoice,
  vcg_orders: exportVcgOrders,
  generic: exportGeneric,
};

export const exportToExcel = (data, filename, fieldsConfig = null, excelTemplateType = null) => {
  const templateKey = excelTemplateType || 'invoice';
  const exportFn = EXPORT_TEMPLATES[templateKey] || exportInvoice;
  exportFn(data, filename, fieldsConfig);
};

function exportInvoice(data, filename, fieldsConfig) {
  const rows = [];

  rows.push(['DATOS DEL DOCUMENTO EXTRAIDO']);
  rows.push([]);

  if (hasSection(fieldsConfig, 'proveedor')) {
    rows.push(['PROVEEDOR']);
    const proveedor = data.proveedor || {};
    rows.push(['Nombre', proveedor.nombre || '']);
    rows.push(['NIF/CIF', proveedor.nif || '']);
    rows.push(['Direccion', proveedor.direccion || '']);
    rows.push(['Telefono', proveedor.telefono || '']);
    rows.push(['Email', proveedor.email || '']);
    rows.push([]);
  }

  if (hasSection(fieldsConfig, 'cliente')) {
    rows.push(['CLIENTE']);
    const cliente = data.cliente || {};
    rows.push(['Nombre', cliente.nombre || '']);
    rows.push(['NIF/CIF', cliente.nif || '']);
    rows.push(['Direccion', cliente.direccion || '']);
    rows.push([]);
  }

  if (hasSection(fieldsConfig, 'documento')) {
    rows.push(['DOCUMENTO']);
    const documento = data.documento || {};
    rows.push(['Tipo', documento.tipo || '']);
    rows.push(['Numero', documento.numero || '']);
    rows.push(['Fecha emision', documento.fecha_emision || '']);
    rows.push(['Fecha vencimiento', documento.fecha_vencimiento || '']);
    rows.push(['Forma de pago', documento.forma_pago || '']);
    rows.push([]);
  }

  if (hasSection(fieldsConfig, 'lineas')) {
    rows.push(['LINEAS DEL DOCUMENTO']);
    rows.push(['Descripcion', 'Cantidad', 'Precio Unitario', 'Descuento', 'IVA %', 'Total Linea']);
    const lineas = data.lineas || [];
    lineas.forEach(linea => {
      rows.push([
        linea.descripcion || '',
        linea.cantidad || '',
        linea.precio_unitario || '',
        linea.descuento || '',
        linea.iva || '',
        linea.total_linea || ''
      ]);
    });
    rows.push([]);
  }

  if (hasSection(fieldsConfig, 'totales')) {
    rows.push(['TOTALES']);
    const totales = data.totales || {};
    rows.push(['Base imponible', totales.base_imponible || '']);
    rows.push(['Total IVA', totales.total_iva || '']);
    rows.push(['TOTAL', totales.total || '']);
  }

  const worksheet = XLSX.utils.aoa_to_sheet(rows);

  worksheet['!cols'] = [
    { wch: 25 },
    { wch: 15 },
    { wch: 15 },
    { wch: 12 },
    { wch: 10 },
    { wch: 15 }
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Documento');

  XLSX.writeFile(workbook, `${filename}.xlsx`);
}

function exportGeneric(data, filename, fieldsConfig) {
  if (!fieldsConfig) return exportInvoice(data, filename, fieldsConfig);

  let config;
  try {
    config = typeof fieldsConfig === 'string' ? JSON.parse(fieldsConfig) : fieldsConfig;
  } catch {
    return exportInvoice(data, filename, fieldsConfig);
  }

  const cardFields = (config.cards || []).flatMap(card =>
    Object.entries(card.fields || {}).map(([key, label]) => ({ key, label }))
  );

  const tableConfig = config.table || null;
  const tableFields = tableConfig
    ? (tableConfig.column_order || Object.keys(tableConfig.fields || {})).map(key => ({
        key,
        label: (tableConfig.fields || {})[key] || key,
      }))
    : [];

  const totalFields = config.totals
    ? Object.entries(config.totals.fields || {}).map(([key, label]) => ({ key, label }))
    : [];

  const headers = [
    ...cardFields.map(f => f.label),
    ...tableFields.map(f => f.label),
    ...totalFields.map(f => f.label),
  ];

  const rows = [headers];

  const arrayKey = tableConfig?.key || 'items';
  const items = tableConfig ? (data[arrayKey] || [{}]) : [{}];

  items.forEach(item => {
    const row = [
      ...cardFields.map(f => formatDate(data[f.key] ?? '')),
      ...tableFields.map(f => formatDate(item[f.key] ?? '')),
      ...totalFields.map(f => formatDate(data[f.key] ?? '')),
    ];
    rows.push(row);
  });

  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  worksheet['!cols'] = headers.map(() => ({ wch: 18 }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

  XLSX.writeFile(workbook, `${filename}.xlsx`);
}

function exportVcgOrders(data, filename) {
  const rows = [VCG_HEADERS];

  const items = data.items || [{}];
  items.forEach(item => {
    rows.push([
      filename,
      data.order_number || '',
      formatDate(data.delivery_date || '') || formatDate(item.delivery_date || ''),
      data.customer_name || '',
      data.customer_address || '',
      item.part_code || '',
      item.quantity || 0,
      item.price || 0,
      data.subtotal || 0,
      data.total || 0,
      data.observaciones || '',
      item.drawing || '',
      data.currency || '',
    ]);
  });

  const worksheet = XLSX.utils.aoa_to_sheet(rows);

  worksheet['!cols'] = VCG_HEADERS.map(() => ({ wch: 18 }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'VCG Orders');

  XLSX.writeFile(workbook, `${filename}.xlsx`);
}
